import React from 'react';
import Modal from '@/components/shared/Modal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HelpContent, HelpSection, HelpSubsection } from './help-content';
import { Info, AlertTriangle, Lightbulb, CheckCircle2 } from 'lucide-react';

import { useIsMobile } from '@/hooks/use-mobile';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: HelpContent;
}

/**
 * Get icon component based on subsection type
 */
const getSubsectionIcon = (type?: string) => {
  switch (type) {
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-status-warn" />;
    case 'tip':
      return <Lightbulb className="h-4 w-4 text-accent" />;
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-status-clear" />;
    case 'info':
    default:
      return <Info className="h-4 w-4 text-muted" />;
  }
};

/**
 * Get background color class based on subsection type
 */
const getSubsectionBgClass = (type?: string) => {
  switch (type) {
    case 'warning':
      return 'bg-status-warn-surface/20 border-status-warn';
    case 'tip':
      return 'bg-status-info-surface/30 border-accent/20';
    case 'success':
      return 'bg-status-clear-surface/20 border-status-clear';
    case 'info':
    default:
      return 'bg-bg border-line';
  }
};

/**
 * Render a subsection with appropriate styling
 */
const SubsectionCard: React.FC<{ subsection: HelpSubsection }> = ({ subsection }) => {
  return (
    <div className={`p-3 rounded-md border ${getSubsectionBgClass(subsection.type)}`}>
      <div className="flex items-start gap-2">
        {subsection.icon && (
          <span className="text-lg mt-0.5 flex-shrink-0">{subsection.icon}</span>
        )}
        {!subsection.icon && (
          <div className="mt-0.5 flex-shrink-0">
            {getSubsectionIcon(subsection.type)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h5 className="font-semibold mb-1 text-sm">{subsection.title}</h5>
          <p className="text-sm text-fg whitespace-pre-line">
            {subsection.content}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Render a help section
 */
const HelpSectionContent: React.FC<{ section: HelpSection }> = ({ section }) => {
  const isTechnical = section.type === 'technical';

  return (
    <div className={`space-y-3 ${isTechnical ? 'p-4 rounded-lg bg-status-info-surface border-2 border-line' : ''}`}>
      {isTechnical && (
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-line">
          <div className="px-2 py-1 bg-accent text-accent-fg text-xs font-semibold rounded uppercase tracking-wide">
            Technical Deep Dive
          </div>
          <p className="text-xs text-accent italic">
            For practitioners familiar with biomechanics methodology
          </p>
        </div>
      )}

      {section.content && (
        <p className={`text-sm whitespace-pre-line ${isTechnical ? 'text-fg font-medium' : 'text-fg'}`}>
          {section.content}
        </p>
      )}

      {section.subsections && section.subsections.length > 0 && (
        <div className="space-y-3">
          {section.subsections.map((subsection, idx) => (
            <SubsectionCard key={idx} subsection={subsection} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * HelpModal Component
 * 
 * Displays contextual help content in an organized, interactive modal
 * Uses accordions for easy navigation through help sections
 */
export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, content }) => {
  const isMobile = useIsMobile();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={content.title}
      maxWidth={isMobile ? "w-screen h-screen max-w-none m-0 rounded-none" : "max-w-4xl"}
      padding={false}
      customHelpContent={{
        title: "Help System Help",
        description: "This is the help system - you're already here!",
        sections: []
      }}
    >
      <div className="flex flex-col h-[80vh] max-h-[80vh]">
        {/* Description */}
        {content.description && (
          <div className="px-6 pt-4 pb-3 border-b border-line bg-status-info-surface/30">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted">
                {content.description}
              </p>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 px-6 py-4">
          <div className="space-y-4 pb-4">
            {content.sections.length > 0 ? (
              <Accordion type="multiple" defaultValue={[`section-0`]} className="w-full">
                {content.sections.map((section, idx) => {
                  const isTechnical = section.type === 'technical';
                  return (
                    <AccordionItem key={idx} value={`section-${idx}`} className={isTechnical ? 'border-line' : ''}>
                      <AccordionTrigger className={`text-left hover:no-underline ${isTechnical ? 'bg-status-info-surface hover:bg-status-info-surface' : ''}`}>
                        <span className={`font-semibold text-base ${isTechnical ? 'text-accent' : ''}`}>
                          {section.title}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pt-2">
                          <HelpSectionContent section={section} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : (
              <div className="text-center text-muted py-8">
                <Info className="h-12 w-12 mx-auto mb-3 text-muted" />
                <p>No help content available for this screen yet.</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with close button */}
        <div className="px-6 py-3 border-t border-line flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-fg hover:bg-surface rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default HelpModal;
